// Composition model for governed images in the Data and History tabs (DES1 "images across Apps,
// Artifacts, exports and Chat"). The image primitive, its states, ratio boxes and zoom are APP's
// shared SDK Image over INS's `ManagedImageProjection` (managed ref by ARCH, VM by ART); this module
// owns only what the tabs add: the observed-at provenance, the "Image changed since" note and the
// partial-capture count. Provenance comes from the projection (`ref`) alone; a refused or stale projection
// carries none, so none is shown or reconstructed.
import { ManagedImageProjection } from '../../../../src/artifacts/images/projection.mjs';
import { ImageFit, ImageRatio } from '../../../../src/artifacts/images/values.mjs';
import type { ManagedImageData } from '../../artifacts/view-models/images';

export type { ManagedImageData };
/** An image as observed at one point is ART's `ManagedImageData`: the INS projection (`ref`), the
 * declared placement, the caption and the current History comparison (`changedSince`: strict
 * `true` shows the note; null is unknown and shows nothing). */
export type ObservedImage = ManagedImageData;

/**
 * ART's projected VM is the source; this only refuses a value that is not that shape (a stale or
 * denied projection carries no data, so it never arrives here). Every part is checked by INS's own
 * codecs; anything else is absent, never guessed.
 */
export function toManagedImageData(value: unknown): ManagedImageData | null {
  if (typeof value !== 'object' || value === null) return null;
  const v = value as Record<string, unknown>;
  const ref = ManagedImageProjection.safeParse(v.ref);
  const ratio = ImageRatio.safeParse(v.ratio);
  const fit = ImageFit.safeParse(v.fit);
  if (!ref.success || !ratio.success || !fit.success) return null;
  if (typeof v.key !== 'string' || v.key === '' || typeof v.cropSafe !== 'boolean') return null;
  if (fit.data === 'cover' && !v.cropSafe) return null;
  if (v.caption !== null && typeof v.caption !== 'string') return null;
  if (v.changedSince !== null && typeof v.changedSince !== 'boolean') return null;
  return {
    key: v.key,
    ref: ref.data,
    ratio: ratio.data,
    fit: fit.data,
    cropSafe: v.cropSafe,
    caption: v.caption,
    changedSince: v.changedSince,
  };
}

/**
 * Where an image is drawn. The viewer opens by `owner` + ART's key, so the same ART key in two places
 * (a Data cell and a History observation) stays two distinct viewer items with their own projection.
 */
export const dataImageOwner = (tableId: string, rowId: string, columnKey: string) =>
  `data:${tableId}:${rowId}:${columnKey}`;
/** A row-history observation is identified by its recorded facts, never its position in the list. */
export const rowHistoryImageOwner = (change: {
  at: string;
  by: string;
  field: string;
  undoToken?: string;
}) => `row-history:${change.at}:${change.by}:${change.field}:${change.undoToken ?? ''}`;
export const historyImageOwner = (eventId: string) => `history:${eventId}`;

/** "Showing 18 of 24 images · 6 not available"; absent when complete, unknown or inconsistent. */
export function captureCounts(
  shown: number,
  total: number,
): { shown: number; missing: number } | null {
  if (![shown, total].every((n) => Number.isSafeInteger(n) && n >= 0)) return null;
  if (shown >= total) return null;
  return { shown, missing: total - shown };
}
